/**
 * Supplier Management Types (Level 1.2)
 *
 * Suppliers are contacts who supply diamonds to our inventory.
 * They do NOT log in (unlike Users/Dealers).
 */
// === HELPER LABELS ===
export const SUPPLIER_TYPE_LABELS = {
    manufacturer: 'Manufacturer',
    wholesaler: 'Wholesaler',
    broker: 'Broker',
    other: 'Other'
};
export const SUPPLIER_STATUS_LABELS = {
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended'
};
export const SUPPLIER_VERIFICATION_LABELS = {
    pending: 'Pending Verification',
    verified: 'Verified',
    rejected: 'Rejected'
};
// === COLOR CODING ===
export const SUPPLIER_STATUS_COLORS = {
    active: '#10b981', // Green
    inactive: '#6b7280', // Gray
    suspended: '#ef4444' // Red
};
export const SUPPLIER_VERIFICATION_COLORS = {
    pending: '#f59e0b', // Amber
    verified: '#10b981', // Green
    rejected: '#ef4444' // Red
};
/**
 * Validates supplier data
 */
export function validateSupplier(supplier) {
    const errors = [];
    // Required fields
    if (!supplier.companyName?.trim()) {
        errors.push({ field: 'companyName', message: 'Company name is required' });
    }
    if (!supplier.contactPerson?.trim()) {
        errors.push({ field: 'contactPerson', message: 'Contact person is required' });
    }
    if (!supplier.email?.trim()) {
        errors.push({ field: 'email', message: 'Email is required' });
    }
    if (!supplier.phone?.trim()) {
        errors.push({ field: 'phone', message: 'Phone is required' });
    }
    if (!supplier.supplierCode?.trim()) {
        errors.push({ field: 'supplierCode', message: 'Supplier code is required' });
    }
    if (!supplier.address?.street?.trim()) {
        errors.push({ field: 'address', message: 'Street address is required' });
    }
    if (!supplier.address?.city?.trim()) {
        errors.push({ field: 'address', message: 'City is required' });
    }
    if (!supplier.address?.state?.trim()) {
        errors.push({ field: 'address', message: 'State is required' });
    }
    if (!supplier.address?.zipCode?.trim()) {
        errors.push({ field: 'address', message: 'Zip code is required' });
    }
    if (!supplier.address?.country?.trim()) {
        errors.push({ field: 'address', message: 'Country is required' });
    }
    // Email format
    if (supplier.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplier.email)) {
        errors.push({ field: 'email', message: 'Invalid email format' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Generates a unique supplier code
 * Format: First 3 letters of company name + 3-digit number
 * Example: Diamond Wholesale Inc -> DIA001
 */
export function generateSupplierCode(companyName, existingCodes) {
    const prefix = companyName
        .replace(/[^a-zA-Z]/g, '')
        .toUpperCase()
        .substring(0, 3)
        .padEnd(3, 'X');
    let counter = 1;
    let code = `${prefix}${counter.toString().padStart(3, '0')}`;
    while (existingCodes.includes(code)) {
        counter++;
        code = `${prefix}${counter.toString().padStart(3, '0')}`;
    }
    return code;
}
/**
 * Formats supplier name for display
 */
export function formatSupplierName(supplier) {
    return `${supplier.supplierCode} - ${supplier.companyName}`;
}
/**
 * Calculates available credit for supplier (if they extend credit to us)
 * This is inverse of customer credit - it's how much more we can owe them
 */
export function calculateSupplierAvailableCredit(supplier, creditLimit) {
    if (!creditLimit)
        return null;
    return Math.max(0, creditLimit - (supplier.totalOwed + supplier.totalMemoDebt));
}
